import { eventsHub } from '@/main';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import { AxiosRequestInterceptor } from '@/models/base/AxiosRequestInterceptor';
import { AxiosResponse } from 'axios';
import {
  AUTHORIZATION_API_SESSION,
  AUTHORIZATION_ROOT,
  REFRESH_TOKEN,
  REFRESH_TOKEN_WS,
  REFRESHING_TOKEN,
  SET_TOKENS,
} from '@/types/authorization.types';
import { $httpClient } from '@/services/httpClient';
import { SET_GLOBAL_ERROR } from '@/types/store.types';
import { AUTHENTICATION_ROOT, SET_AUTH_LOADING_MODE } from '@/types/authentication.types';
import { SYSTEM_ROOT, TOKEN } from '@/types/system.types';
import * as Sentry from '@sentry/browser';

export const authErrorInterceptor = {
  onFulFilled: (response: AxiosResponse) => {
    if (response && response.data && typeof response.data === 'string'
      && response.data.includes('Your support ID is')) {
      response.status = 400;
      if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_SENTRY_AUTH_TOKEN) {
        Sentry.captureException(response.data);
      }
      eventsHub.$store.commit(SET_GLOBAL_ERROR, { response });
    }
    eventsHub.$store.commit(AUTHENTICATION_ROOT + SET_AUTH_LOADING_MODE, false, { root: true });
    return response;
  },
  onError: originalError => {
    let isAuth = false;
    let isLobbyAccessReq = false;
    originalError = originalError || { code: 'ECONNABORTED' };
    let error = { ...originalError };
    if (!originalError.code && !originalError.response) {
      error = { ...originalError, code: originalError.code || 'ECONNABORTED' };
    }
    isAuth = error.config ? error.config.url.includes('auth') : false;
    if (error.code === 'ECONNABORTED') {
      error.response = {
        status: 'NO_RESPONSE',
        data: { messages: [eventsHub.$t('login.network_error')] },
      };
    }
    if (!error.response) {
      error.response = { status: 'NO_RESPONSE' };
    }
    isLobbyAccessReq = error.config ? error.config.url.includes('lobbyAccessible') : false;
    return new Promise((resolve, reject) => {
      switch (error.response.status) {
        case 'ERR_ASSERTION':
          // do something on request timeout
          break;
        case 'NO_RESPONSE':
          isAuth ? reject(error) : eventsHub.$store.commit(SET_GLOBAL_ERROR, error);
          break;
        case 401:
        case 403:
          if (isLobbyAccessReq) {
            reject(error);
          } else {
            eventsHub.$store.commit(SET_GLOBAL_ERROR, error);
          }
          break;
        case 404:
          eventsHub.$router.push({ name: 'not-found' });
          break;
        case 500:
          eventsHub.$store.commit(SET_GLOBAL_ERROR, error);
          break;
        case 503:
          eventsHub.$router.push({ name: 'maintenance' });
          break;
        default:
          reject(error.response.data);
          break;
      }
    });
  },
};

export const refreshTokenInterceptor = {
    onFulFilled: (response: AxiosResponse) => {
      return response;
    },
    onError: error => {
      const isRefreshingToken = eventsHub.$store.getters[AUTHORIZATION_ROOT + REFRESHING_TOKEN];
      if (!error.response || error.response.status !== 401 || isRefreshingToken) {
        return Promise.reject(error);
      }
      const originalRequest = { ...error.config };
      const refreshToken = eventsHub.$store.getters[AUTHORIZATION_ROOT + REFRESH_TOKEN];
      return new Promise((resolve, reject) => {
        $httpClient.post(AUTHORIZATION_API_SESSION + REFRESH_TOKEN_WS, { refreshToken })
          .subscribe(
            data => {
              eventsHub.$store.commit(AUTHORIZATION_ROOT + SET_TOKENS, data);
              $httpClient.request(originalRequest).subscribe(
                res => {
                  return resolve(res);
                },
                err => {
                  return reject({ response: { data: err } });
                },
              );
            },
          );
      });
    },
  };

export const accessTokenInterceptor: AxiosRequestInterceptor = {
  onConfig: (config) => {
    const token = eventsHub.$store.getters[StoreNamespace.ACCESS_TOKEN_GETTER];
    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    }
    return config;
  },
  onError: error => {
    Promise.reject(error);
  },
};


export const systemInfoTokenInterceptor: AxiosRequestInterceptor = {
  onConfig: (config) => {
    const token = eventsHub.$store.getters[SYSTEM_ROOT + TOKEN];

    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    }

    return config;
  },
  onError: error => {
    Promise.reject(error);
  },
};


